import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist'
import { Button, LoadingState } from '../components/ui'

type Destination = string | unknown[]
interface PageLink { destination: Destination; left: number; top: number; width: number; height: number }
interface PageResult { key: string; links: PageLink[]; error?: string }

/** One rendered page keeps even a thousand-page report usable on a phone. */
export function PdfReportViewer({ document, title }: { document: PDFDocumentProxy; title: string }) {
  const [pageNumber, setPageNumber] = useState(1)
  const [pageInput, setPageInput] = useState('1')
  const [width, setWidth] = useState(0)
  const [attempt, setAttempt] = useState(0)
  const [result, setResult] = useState<PageResult | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const pageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const renderKey = `${pageNumber}:${width}:${attempt}`
  const currentResult = result?.key === renderKey ? result : null
  const requestedPage = Number(pageInput)
  const validPage = Number.isInteger(requestedPage) && requestedPage >= 1 && requestedPage <= document.numPages

  useEffect(() => {
    const host = pageRef.current
    if (!host) return
    const measure = () => setWidth(Math.max(1, Math.floor(host.clientWidth || host.getBoundingClientRect().width)))
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(host)
    window.addEventListener('resize', measure)
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure) }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || width <= 0) return
    let active = true
    let page: PDFPageProxy | null = null
    let renderTask: RenderTask | null = null

    async function renderPage() {
      try {
        page = await document.getPage(pageNumber)
        if (!active) return
        const original = page.getViewport({ scale: 1 })
        const viewport = page.getViewport({ scale: width / original.width })
        // Cap backing pixels on high-DPI phones; CSS still fits the available width.
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(8_000_000 / (viewport.width * viewport.height)))
        canvas!.width = Math.ceil(viewport.width * pixelRatio)
        canvas!.height = Math.ceil(viewport.height * pixelRatio)
        renderTask = page.render({ canvas: canvas!, viewport, transform: pixelRatio === 1 ? undefined : [pixelRatio, 0, 0, pixelRatio, 0, 0] })
        const [, annotations] = await Promise.all([renderTask.promise, page.getAnnotations({ intent: 'display' }) as Promise<unknown[]>])
        if (!active) return
        const links: PageLink[] = []
        for (const annotation of annotations) {
          if (!annotation || typeof annotation !== 'object') continue
          const entry = annotation as Record<string, unknown>
          // Only internal GoTo links are interactive. Never run PDF scripts or external actions.
          if (entry.subtype !== 'Link' || (typeof entry.dest !== 'string' && !Array.isArray(entry.dest)) || !Array.isArray(entry.rect) || entry.rect.length !== 4 || !entry.rect.every((value) => typeof value === 'number' && Number.isFinite(value))) continue
          const [left, bottom, right, top] = entry.rect as number[]
          const [x1, y1] = viewport.convertToViewportPoint(left, bottom)
          const [x2, y2] = viewport.convertToViewportPoint(right, top)
          links.push({ destination: entry.dest, left: Math.min(x1, x2) / viewport.width * 100, top: Math.min(y1, y2) / viewport.height * 100, width: Math.abs(x2 - x1) / viewport.width * 100, height: Math.abs(y2 - y1) / viewport.height * 100 })
        }
        setResult({ key: renderKey, links })
      } catch {
        if (active) setResult({ key: renderKey, links: [], error: 'Страница не отобразилась. Повторите открытие страницы или скачайте PDF.' })
      } finally {
        page?.cleanup()
      }
    }
    void renderPage()
    return () => { active = false; renderTask?.cancel() }
  }, [document, pageNumber, width, renderKey])

  function goToPage(next: number) {
    if (!Number.isInteger(next) || next < 1 || next > document.numPages) return
    setPageNumber(next)
    setPageInput(String(next))
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }

  async function followDestination(destination: Destination) {
    try {
      const explicit = typeof destination === 'string' ? await document.getDestination(destination) : destination
      const target: unknown = explicit?.[0]
      if (typeof target === 'number') goToPage(target + 1)
      else if (target && typeof target === 'object' && 'num' in target && 'gen' in target && Number.isInteger(target.num) && Number.isInteger(target.gen)) {
        goToPage(await document.getPageIndex(target as Parameters<PDFDocumentProxy['getPageIndex']>[0]) + 1)
      }
    } catch { /* An unavailable link must not stop reading the current page. */ }
  }

  function submitPage(event: FormEvent) { event.preventDefault(); if (validPage) goToPage(requestedPage) }

  return <section className="report-pdf" aria-label={title}>
    <form className="report-pdf__toolbar" onSubmit={submitPage} aria-label="Страницы отчёта">
      <Button type="button" variant="quiet" disabled={pageNumber === 1} onClick={() => goToPage(pageNumber - 1)} aria-label="Предыдущая страница"><ChevronLeft size={18} /></Button>
      <label>Страница <input type="number" inputMode="numeric" min={1} max={document.numPages} value={pageInput} onChange={(event) => setPageInput(event.target.value)} aria-label="Номер страницы" /></label>
      <span>из {document.numPages}</span>
      <Button type="submit" variant="secondary" disabled={!validPage || requestedPage === pageNumber}>Перейти</Button>
      <Button type="button" variant="quiet" disabled={pageNumber === document.numPages} onClick={() => goToPage(pageNumber + 1)} aria-label="Следующая страница"><ChevronRight size={18} /></Button>
    </form>
    <div className="report-pdf__scroll" ref={scrollRef}>
      {!currentResult && <LoadingState label={`Открываем страницу ${pageNumber}`} />}
      {currentResult?.error && <div className="error-state" role="alert"><p>{currentResult.error}</p><Button variant="secondary" onClick={() => setAttempt((value) => value + 1)}><RefreshCw size={16} />Повторить страницу</Button></div>}
      <div className="report-pdf__page" ref={pageRef}>
        <canvas key={renderKey} ref={canvasRef} className="report-pdf__canvas" hidden={!currentResult || Boolean(currentResult.error)} role="img" aria-label={`Страница ${pageNumber} из ${document.numPages}`} />
        {currentResult?.links.map((link, index) => <button key={index} type="button" className="report-pdf__link" style={{ left: `${link.left}%`, top: `${link.top}%`, width: `${link.width}%`, height: `${link.height}%` }} aria-label={`Перейти к разделу отчёта ${index + 1}`} onClick={() => void followDestination(link.destination)} />)}
      </div>
    </div>
  </section>
}
